import _trim from 'lodash/trim';

export function truncate(str: string, n = 20) {
  return str.length > n ? str.substring(0, n - 1) + '...' : str;
}

export function getNameFromEmail(str: string) {
  if (typeof str !== 'string') {
    return null;
  }
  return str.split('@')[0];
}

export function startsWith(str: string, x: string) {
  return !!x && x.length > 0 && str.indexOf(x) === 0;
}

/**
 * Ensures a string starts with the given prefix.
 *
 * @param str - The string to check
 * @param prefix - The prefix to ensure
 * @returns The string with the prefix guaranteed
 *
 * @example
 * ensurePrefix('123', 'IND-') // returns 'IND-123'
 * ensurePrefix('IND-123', 'IND-') // returns 'IND-123'
 */
export function ensurePrefix(str: string, prefix: string): string {
  return str.startsWith(prefix) ? str : `${prefix}${str}`;
}

export function isValidIp(value: string) {
  // it will fail any string with a newline
  const ipRegex =
    /((?!.*\n)^\s*((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))\s*$)|(^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$)/gm;
  return ipRegex.test(value);
}

export function isIPV6(value: string) {
  return isValidIp(value) && value.includes(':');
}

export function isJA3Hash(value: string) {
  const ja3Regex = /^[a-f0-9]{32}$/i;
  return typeof value === 'string' && ja3Regex.test(value);
}

export function isScopedIpEntity(value: string) {
  if (typeof value !== 'string' || !value.includes('/')) return false;
  const [ip, scope] = value.split('/', 2);
  return isValidIp(ip) && /^[a-zA-Z0-9-]+$/.test(scope);
}

export function stripFlowlogsSubnetFromIp(value: string) {
  if (value.includes('/')) {
    return value.split('/')[0];
  }
  return value;
}

export const maybePlural = (value: number, label: string, pluralLabel?: string) =>
  Math.abs(value) === 1 ? label : (pluralLabel ?? `${label}s`);

export const encodeKeyForRecharts = (key: string | number | undefined) => {
  return String(key).replace(/\./g, '^-^');
};

export const decodeKeyForRecharts = (key: string) => {
  return String(key).replace(/\^-\^/g, '.');
};

export const toLowercaseAlphanumeric = (str: string) => {
  return _trim(str.replace(/[^a-z0-9]+/gi, '-').toLowerCase(), '-');
};
